/**
 * 로컬 개발 전용: `.env.local`에 액세스 토큰·userId를 넣으면
 * `medicrew_access_token` / `medicrew_user`를 주입해 실 API 계정으로 테스트합니다.
 *
 * - `localhost` / `127.0.0.1` + `NODE_ENV === 'development'` 에서만 동작
 * - 기본: 저장된 `medicrew_user`가 없을 때만 적용
 * - `REACT_APP_DEV_TOKEN_OVERRIDE=true` 이면 저장된 세션을 덮어씀
 *
 * salt 등 별도 값은 프론트 인증 경로에서 사용하지 않음(백엔드/도구 전용 시 문서만).
 */

import apiClient from '@/services/apiClient';

type DevUserRole = 'master' | 'student' | 'guest' | 'admin';

interface DevBootstrapAccount {
	accountId: string;
	organizationId: string | null;
	organizationName: string;
	role: DevUserRole;
	license: string;
	isActive: boolean;
}

interface DevBootstrapUser {
	userId: number;
	name: string;
	email: string;
	profileImageUrl?: string | null;
	guestExpirationDate?: string | null;
	isAuthenticated: boolean;
	currentAccount: Omit<DevBootstrapAccount, 'isActive'>;
	accounts: DevBootstrapAccount[];
}

function isLocalDevHost(): boolean {
	if (process.env.NODE_ENV !== 'development') return false;
	if (typeof window === 'undefined') return false;
	const h = window.location.hostname;
	return h === 'localhost' || h === '127.0.0.1';
}

function parseDevUserRole(): DevUserRole {
	const v = process.env.REACT_APP_DEV_USER_ROLE?.trim().toLowerCase();
	if (v === 'master' || v === 'student' || v === 'guest' || v === 'admin') return v;
	return 'student';
}

export interface DevAccessTokenBootstrapResult {
	applied: boolean;
	user: DevBootstrapUser | null;
}

/**
 * 환경 변수에 토큰·userId가 있고 호스트가 로컬이면 주입 시도.
 * @param hadSavedUser - 초기화 시점에 파싱된 저장 사용자 존재 여부(스테일 제거 후)
 */
export function applyDevAccessTokenBootstrap(hadSavedUser: boolean): DevAccessTokenBootstrapResult {
	if (!isLocalDevHost()) {
		return { applied: false, user: null };
	}

	const token = process.env.REACT_APP_DEV_ACCESS_TOKEN?.trim();
	const userId = process.env.REACT_APP_DEV_USER_ID?.trim();
	if (!token || !userId) {
		return { applied: false, user: null };
	}

	const override = process.env.REACT_APP_DEV_TOKEN_OVERRIDE === 'true';
	if (hadSavedUser && !override) {
		return { applied: false, user: null };
	}

	const role = parseDevUserRole();
	const organizationId = process.env.REACT_APP_DEV_ORGANIZATION_ID?.trim() || null;
	const organizationName = process.env.REACT_APP_DEV_ORGANIZATION_NAME?.trim() || '';
	const accountId = process.env.REACT_APP_DEV_ACCOUNT_ID?.trim() || userId;
	const account: DevBootstrapAccount = {
		accountId,
		organizationId,
		organizationName,
		role,
		license: process.env.REACT_APP_DEV_LICENSE?.trim() || 'basic_class',
		isActive: true,
	};
	const user: DevBootstrapUser = {
		userId: parseInt(userId, 10) || 0,
		name: process.env.REACT_APP_DEV_USER_NAME?.trim() || userId,
		email: process.env.REACT_APP_DEV_USER_EMAIL?.trim() || '',
		profileImageUrl: null,
		guestExpirationDate: null,
		isAuthenticated: true,
		currentAccount: {
			accountId: account.accountId,
			organizationId: account.organizationId,
			organizationName: account.organizationName,
			role: account.role,
			license: account.license,
		},
		accounts: [account],
	};

	apiClient.setTokens(token);
	localStorage.setItem('medicrew_user', JSON.stringify(user));

	return { applied: true, user };
}
